import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { bibliothequeService, Marabout, Livre } from '../services/bibliothequeService';

export const MaraboutDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [marabout, setMarabout] = useState<Marabout | null>(null);
  const [livres, setLivres] = useState<Livre[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadMarabout();
      loadLivres();
    }
  }, [id]);

  const loadMarabout = async () => {
    if (!id) return;
    try {
      const data = await bibliothequeService.getMaraboutById(parseInt(id));
      setMarabout(data);
    } catch (error) {
      console.error('Erreur:', error);
      navigate('/bibliotheque');
    } finally {
      setLoading(false);
    }
  };

  const loadLivres = async () => {
    if (!id) return;
    try {
      const data = await bibliothequeService.getLivres(1, 6, { auteur: parseInt(id) });
      setLivres(data.results || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-20 w-20 border-4 border-amber-200 border-t-amber-700"></div>
      </div>
    );
  }

  if (!marabout) {
    return null;
  }

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-white via-amber-50/30 to-green-50/50 overflow-hidden">
      {/* Motifs décoratifs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
      </div>

      {/* Hero Section */}
      <section className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/bibliotheque"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-amber-700 mb-6 transition-colors"
          >
            <span>←</span>
            <span>Retour à la bibliothèque</span>
          </Link>

          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-amber-100/50 overflow-hidden">
            {/* Header avec photo ou gradient */}
            <div className={`relative h-64 ${marabout.photo ? '' : 'bg-gradient-to-br from-amber-500 to-amber-600'}`}>
              {marabout.photo ? (
                <img
                  src={marabout.photo}
                  alt={marabout.nom_complet}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-8xl opacity-50">👳</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h1 className="text-4xl sm:text-5xl font-bold mb-2">{marabout.nom_complet}</h1>
                {marabout.nom_arabe && (
                  <p className="text-2xl text-right" dir="rtl">{marabout.nom_arabe}</p>
                )}
              </div>
            </div>

            {/* Informations principales */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {marabout.periode && (
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-6 border border-amber-200/50">
                    <div className="text-3xl mb-2">📅</div>
                    <h3 className="font-semibold text-gray-700 mb-1">Période</h3>
                    <p className="text-lg font-bold text-amber-900">{marabout.periode}</p>
                  </div>
                )}
                {marabout.date_naissance && (
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200/50">
                    <div className="text-3xl mb-2">🎂</div>
                    <h3 className="font-semibold text-gray-700 mb-1">Naissance</h3>
                    <p className="text-lg font-bold text-green-900">{marabout.date_naissance}</p>
                  </div>
                )}
                {marabout.lieu_naissance && (
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200/50">
                    <div className="text-3xl mb-2">📍</div>
                    <h3 className="font-semibold text-gray-700 mb-1">Lieu de naissance</h3>
                    <p className="text-lg font-bold text-blue-900">{marabout.lieu_naissance}</p>
                  </div>
                )}
                {marabout.nombre_livres > 0 && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200/50">
                    <div className="text-3xl mb-2">📚</div>
                    <h3 className="font-semibold text-gray-700 mb-1">Livres</h3>
                    <p className="text-lg font-bold text-purple-900">{marabout.nombre_livres}</p>
                  </div>
                )}
              </div>

              {/* Biographie */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200/50 mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center space-x-3">
                  <span>📖</span>
                  <span>Biographie</span>
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {marabout.biographie}
                  </p>
                </div>
              </div>

              {/* Livres du marabout */}
              {livres.length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center space-x-3">
                    <span>📚</span>
                    <span>Livres ({livres.length})</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {livres.map((livre) => (
                      <div
                        key={livre.id}
                        className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-blue-100/50"
                      >
                        <div className="relative h-40 bg-gradient-to-br from-blue-500 to-blue-600">
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-6xl opacity-50">📚</span>
                          </div>
                          {livre.couverture && (
                            <img
                              src={livre.couverture}
                              alt={livre.titre}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div className="p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">{livre.titre}</h3>
                          <div className="space-y-1 mb-4 text-sm text-gray-600">
                            <p>🌐 {livre.langue}</p>
                            {livre.nombre_pages && <p>📄 {livre.nombre_pages} pages</p>}
                          </div>
                          {livre.fichier_pdf && (
                            <a
                              href={livre.fichier_pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-center transition-all duration-300"
                            >
                              📥 Télécharger PDF
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

